import { cn } from '@distribute/frontend/utils';

import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Route, Switch } from 'react-router-dom';
import { TEMPLATES_ROUTE } from '../../../entities/history';
import { TemplatesPageList } from './TemplatesPageList';
import { useDispatch } from 'react-redux';

import { Tabs } from '../../../shared/ui';
import { navigate } from '../../../processes/navigation';
import { IconMap } from '../../../shared/sprite';

import { TemplatesTab, SORT_ITEMS } from '../config';
import { TemplatesActions } from './TemplatesActions';
import { NoCustomTemplates } from './NoCustomTemplates';

import { useSplitTemplates, useShareTemplateHandler } from '../hooks';
import { useMobileRedirect } from '../../../shared/hooks/useMobileRedirect';
import { useSelectedTab } from '../../../entities/history';
import { FILTER_TEMPLATE_ITEMS } from '../../../entities/templates';

import { TeamOnlyAlert, useShareParams } from '../../../entities/share';

import { ALL_OWNERS } from '../../../features/team-members';
import { TemplatesFilters } from './TemplatesFilters';

export const TemplatesPage = () => {
  const dispatch = useDispatch();

  const [ownerFilter, setOwnerFilter] = useState(ALL_OWNERS);
  const [filter, setFilter] =
    useState<keyof typeof FILTER_TEMPLATE_ITEMS>('allTypes');
  const [sort, setSort] = useState<keyof typeof SORT_ITEMS>('lastCreated');

  useMobileRedirect();

  const {
    personal: personalTemplates,
    team: teamTemplates,
    drafts: draftsTemplates,
  } = useSplitTemplates();

  const { secondTeam } = useShareParams();
  const { isForbidden, isSharedDistributeTemplate } =
    useShareTemplateHandler(teamTemplates);

  const tabs = useMemo(
    () => [
      {
        name: TemplatesTab.TEAM,
        title: 'Team',
        icon: IconMap.TeamAccess,
        component: (
          <TemplatesPageList
            templates={teamTemplates}
            type={TemplatesTab.TEAM}
            ownerFilter={ownerFilter}
            filter={filter}
            sort={sort}
          />
        ),
      },
      {
        name: TemplatesTab.PERSONAL,
        title: 'Personal',
        icon: IconMap.EyeCrossedBold,
        component: (
          <TemplatesPageList
            templates={personalTemplates}
            type={TemplatesTab.PERSONAL}
            ownerFilter={ownerFilter}
            filter={filter}
            sort={sort}
          />
        ),
      },
      {
        name: TemplatesTab.DRAFTS,
        title: 'Drafts',
        icon: IconMap.DraftPencil,
        component: (
          <TemplatesPageList
            templates={draftsTemplates}
            type={TemplatesTab.DRAFTS}
            ownerFilter={ownerFilter}
            filter={filter}
            sort={sort}
          />
        ),
      },
    ],
    [
      personalTemplates,
      teamTemplates,
      draftsTemplates,
      ownerFilter,
      filter,
      sort,
    ]
  );

  const { selectedTab } = useSelectedTab(tabs);

  const hasAnyTemplates =
    personalTemplates.length > 0 ||
    teamTemplates.length > 0 ||
    draftsTemplates.length > 0;

  return (
    <>
      <Helmet titleTemplate="Templates - Distribute" />
      {secondTeam && !isSharedDistributeTemplate ? (
        <TeamOnlyAlert
          text={
            <>
              The item you are looking for is available only to members of the
              <b>{secondTeam.name}</b> team. Please switch to that team first.
            </>
          }
        />
      ) : (
        isForbidden && <TeamOnlyAlert />
      )}
      <div className="sm:px-1 sm:pt-1 min-w-0 flex flex-col flex-1 gap-y-4">
        <div className="flex items-center justify-between gap-x-3">
          <h1 className="font-medium text-gray-900 text-display-sm font-display">
            Templates
          </h1>
          {hasAnyTemplates && <TemplatesActions />}
        </div>
        {hasAnyTemplates ? (
          <div className="flex flex-col flex-grow-1 gap-y-6 sm:pb-0 pb-6">
            <div
              className={cn(
                'flex flex-nowrap items-end gap-4 md:justify-end md:flex-col min-h-10'
              )}
            >
              <Tabs
                selectedTab={selectedTab}
                tabs={tabs}
                tabsOnly
                onSelectTab={(tab) =>
                  dispatch(navigate({ to: `${TEMPLATES_ROUTE}/${tab.name}` }))
                }
                className="!mb-0"
              />
              <TemplatesFilters
                type={selectedTab.name as TemplatesTab}
                filterByOwner={ownerFilter}
                sort={sort}
                filter={filter}
                onOwnerChange={setOwnerFilter}
                onSortChange={setSort}
                onFilterChange={setFilter}
              />
            </div>
            <Switch>
              {tabs.map((tab) => {
                return (
                  <Route
                    path={`${TEMPLATES_ROUTE}/${tab.name}`}
                    key={tab.name}
                    render={() => tab.component}
                  />
                );
              })}
            </Switch>
          </div>
        ) : (
          <NoCustomTemplates />
        )}
      </div>
    </>
  );
};
